import React from 'react';
import { Platform } from 'react-native';

import { Box } from '@components//Restyle';
import {
  capitalizeFirstLetter,
  ChangeGroup,
  ChangeItem,
  getTaskAssignedRole,
} from '@components/ActivityList/activityHelpers';
import { ActivityItemDocuments } from '@components/ActivityList/ActivityItemDocuments';
import ChatMessage from '@components/Chat/ChatMessage';
import TagsCollection from '@components/Chat/TagsCollection';
import {
  Audit,
  Attachment,
  ProjectUser,
  Task,
  TaskUser,
  EntityUser,
  Message,
  Document,
  Maybe,
} from '@graphql/generated';
import i18n from '@i18n/i18n.config';
import { generateTagCollections } from '@utils/tagCollectionService';

export type { ChangeGroup };

/**
 * Generate a human-readable activity message based on the project activity type and related data.
 */
export const getProjectActivityMessage = (
  event: ProjectActivityType,
  projectRole?: string,
  messageBody?: string,
  changes?: ChangeGroup[],
  attachment?: Maybe<Attachment>,
  username?: string
) => {
  try {
    switch (event) {
      case ProjectActivityType.ProjectCreated:
        return 'Created this project';

      case ProjectActivityType.ProjectUpdated:
        return handleProjectUpdated(changes);

      case ProjectActivityType.ProjectReassigned:
        return handleProjectReassigned(changes, attachment);

      case ProjectActivityType.ProjectAssigned:
        return (
          'Added ' + (username ?? 'someone') + ' as Project ' + projectRole
        );
      case ProjectActivityType.ProjectUnassigned:
        return 'Unassigned ' + (username ?? 'someone') + ' from this project';

      case ProjectActivityType.ProjectUnassignedSelf:
        return 'Left this Project';

      case ProjectActivityType.ProjectArchived:
        return 'Archived this project';

      case ProjectActivityType.ProjectArchiveRestored:
        return 'Restored this project';

      case ProjectActivityType.TaskCompleted:
        return 'Completed this task';

      case ProjectActivityType.TaskAssigned:
        return (
          'Added ' +
          (username ?? 'someone') +
          ' as Task ' +
          getTaskAssignedRole(changes)
        );

      case ProjectActivityType.TaskDeleted:
        return 'Deleted by ' + (username ?? 'someone');

      case ProjectActivityType.TaskCreated:
        return 'Created this task';

      case ProjectActivityType.TaskCompletedRestore:
        return 'Restored this task';

      case ProjectActivityType.TaskUnassigned:
        return 'Unassigned ' + (username ?? 'someone') + ' from this task';

      case ProjectActivityType.TaskUnassignedSelf:
        return 'Left this task';

      case ProjectActivityType.TaskReassigned: {
        const fromRole = capitalizeFirstLetter(changes?.[0]?.roles?.from || '');
        const toRole = capitalizeFirstLetter(changes?.[0]?.roles?.to || '');
        return `Changed ${
          username ?? '@(Removed User)'
        } role from ${fromRole} to ${toRole}`;
      }

      case ProjectActivityType.ProjectDocument:
        return 'Added file to this project';

      case ProjectActivityType.TaskDocument:
        return 'Added file to this task';

      case ProjectActivityType.MessageSent:
        return messageBody;

      case ProjectActivityType.Checkin:
        return i18n.t('models:chat.checkin.readyToWork');

      default:
        return '';
    }
  } catch (e) {
    console.error('getProjectActivityMessage error:', e);
    return '';
  }
};

/**
 * Handle ProjectUpdated event message generation.
 * Combines all project-related changes into a readable format.
 */
export const handleProjectUpdated = (changes?: ChangeGroup[]): string => {
  const projectChanges = changes?.[0]?.project || [];
  if (!projectChanges.length) return 'Project has been updated';

  const messages: string[] = projectChanges.map((change) => {
    switch (change.type) {
      case 'name':
        return getNameChange(change);
      case 'location':
        return getLocation(change);
      case 'team':
        return getTeam(change);
      default:
        return '';
    }
  });

  return messages.filter(Boolean).join('\n');
};

/**
 * Handle ProjectReassigned event message generation.
 * Formats user role change in a readable sentence.
 */
export const handleProjectReassigned = (
  changes?: ChangeGroup[],
  attachment?: Maybe<Attachment>
): string => {
  const fromRole = capitalizeFirstLetter(changes?.[0]?.roles?.from || '');
  const toRole = capitalizeFirstLetter(changes?.[0]?.roles?.to || '');

  const userName = attachment?.user?.name || '';
  return `Changed @${
    userName || '(Removed User)'
  } role from ${fromRole} to ${toRole}`;
};

export { getTaskAssignedRole };

/**
 * Format project name change message.
 * When both previous and new names are available, include both so the
 * activity log shows the full transition. Falls back to the new-name-only
 * wording when `from` is missing (e.g. if a prior event didn't capture it).
 */
const getNameChange = (project?: ChangeItem): string => {
  if (project?.from && project?.to) {
    return `Project name has been updated from ${project.from} to ${project.to}.`;
  }
  if (project?.to) {
    return `Project name has been changed to ${project.to}.`;
  }
  return '';
};

/**
 * Format location change message.
 */
const getLocation = (project?: ChangeItem): string => {
  if (project?.from && project?.to) {
    return `Project location has been changed to ${project.to}.`;
  } else if (!project?.from && project?.to) {
    return `Project location: ${project.to}.`;
  } else if (project?.from && !project?.to) {
    return 'Project location has been deleted.';
  }
  return '';
};

/**
 * Format team change message.
 */
const getTeam = (project?: ChangeItem): string => {
  return `Project team has been changed to ${project?.to}.`;
};

export enum ProjectActivityType {
  TaskDocument = 'TaskDocument',
  ProjectDocument = 'ProjectDocument',
  ProjectCreated = 'ProjectCreated',
  ProjectUpdated = 'ProjectUpdated',
  ProjectAssigned = 'ProjectAssigned',
  ProjectReassigned = 'ProjectReassigned',
  ProjectUnassigned = 'ProjectUnassigned',
  ProjectUnassignedSelf = 'ProjectUnassignedSelf',
  ProjectCompleted = 'ProjectCompleted',
  ProjectArchived = 'ProjectArchived',
  ProjectArchiveRestored = 'ProjectArchiveRestored',
  MessageSent = 'MessageSent',
  TaskCreated = 'TaskCreated',
  TaskAssigned = 'TaskAssigned',
  TaskDeleted = 'TaskDeleted',
  TaskUnassigned = 'TaskUnassigned',
  TaskUnassignedSelf = 'TaskUnassignedSelf',
  TaskCompleted = 'TaskCompleted',
  TaskCompletedRestore = 'TaskCompletedRestore',
  TaskReassigned = 'TaskReassigned',
  Checkin = 'Checkin',
  Default = '',
}

const allowedActivities = [
  ProjectActivityType.ProjectDocument,
  ProjectActivityType.ProjectCreated,
  ProjectActivityType.ProjectUpdated,
  ProjectActivityType.ProjectAssigned,
  ProjectActivityType.ProjectReassigned,
  ProjectActivityType.ProjectUnassigned,
  ProjectActivityType.ProjectUnassignedSelf,
  ProjectActivityType.ProjectArchived,
  ProjectActivityType.ProjectArchiveRestored,
  ProjectActivityType.TaskCompleted,
  ProjectActivityType.TaskAssigned,
  ProjectActivityType.TaskDeleted,
  ProjectActivityType.TaskCreated,
  ProjectActivityType.TaskCompletedRestore,
  ProjectActivityType.TaskUnassigned,
  ProjectActivityType.TaskUnassignedSelf,
  ProjectActivityType.TaskReassigned,
  ProjectActivityType.MessageSent,
  ProjectActivityType.Checkin,
];

export const filteredProjectAudits = (audits: Audit[]) => {
  return audits.filter((audit) =>
    allowedActivities.includes(audit.event as ProjectActivityType)
  );
};

type ProjectActivityItemProps = {
  audit: Audit;
  doNotUseGlobalSearch?: boolean;
  filterVal?: string;
  externalNavigate?: any;
  /** Pass true when rendering inside the Global Activity feed so that
   *  TagsCollection uses activity-feed display rules (horizontal layout,
   *  no outer border, 20-char truncation, no deleted-task message).
   *  Web-only — has no effect on Android/iOS. */
  activityMode?: boolean;
  isProject?: boolean;
};

const ProjectActivityItem: React.FC<ProjectActivityItemProps> = ({
  audit,
  filterVal,
  doNotUseGlobalSearch = false,
  externalNavigate,
  activityMode = false,
  isProject = false,
}) => {
  const { attachment, user } = audit;
  // borderless and activityMode are web-only visual treatments
  const isWeb = Platform.OS === 'web';

  const isTaskAssigned = audit.event === 'TaskAssigned';
  const isTaskDeleted = audit.event === 'TaskDeleted';
  const isTaskUnassigned = audit.event === 'TaskUnassigned';
  const isTaskUnassignedSelf = audit.event === 'TaskUnassignedSelf';
  const isTaskReassigned = audit.event === 'TaskReassigned';

  const isProjectAssigned = [
    ProjectActivityType.ProjectAssigned,
    ProjectActivityType.ProjectReassigned,
    ProjectActivityType.ProjectUnassigned,
    ProjectActivityType.ProjectUnassignedSelf,
  ].includes(audit.event);

  const isMessageType = (item: Attachment): item is Message =>
    item.__typename === 'Message';

  const _isProjectType = (item: Attachment): item is ProjectUser =>
    item.__typename === 'Project';

  const isTaskType = (item: Attachment): item is Task =>
    item.__typename === 'Task';

  const isTaskUserType = (item: Attachment): item is TaskUser =>
    item.__typename === 'TaskUser';

  const isEntityType = (item: Attachment): item is EntityUser =>
    item.__typename === 'EntityUser';

  const isDocumentType = (item: Attachment): item is Document =>
    item.__typename === 'Document';

  if (!attachment) {
    return null;
  }

  if (isMessageType(attachment)) {
    const { attachments, checkin } = attachment;

    if (
      attachments?.filter(
        (a) =>
          a.__typename !== 'Document' &&
          a.__typename !== 'MessageProject' &&
          a.__typename !== 'MessageTask'
      ).length === 0 ||
      checkin
    ) {
      const tagsByAuthorCollections = generateTagCollections(attachments || []);
      const bottom = isWeb ? 'xxs' : 'xs';
      const checkIn =
        checkin &&
        isProject &&
        attachments?.filter((a) => a.__typename === 'MessageTask').length === 0;
      return (
        <Box>
          <Box
            marginBottom={checkIn ? 'none' : bottom}
            pb={isWeb && !checkIn ? 'xxxs' : 'none'}>
            <ChatMessage
              doNotUseGlobalSearch={doNotUseGlobalSearch}
              externalNavigate={externalNavigate}
              filterVal={filterVal ?? ''}
              message={attachment}
              isActivityItem
              activityActor={user}
              isProject={isProject}
            />
          </Box>
          {tagsByAuthorCollections.map((tagCollections) => {
            return tagCollections.map((item) => {
              const { project, tasks, author } = item;
              return (
                <Box key={project?.id}>
                  <TagsCollection
                    doNotUseGlobalSearch={doNotUseGlobalSearch}
                    externalNavigate={externalNavigate}
                    filterVal={filterVal ?? ''}
                    tagsCollection={{ project, tasks, author }}
                    showAuthor={false}
                    borderless
                    activityMode={activityMode}
                    isRow
                    isActivityItem
                    showProject={!isProject}
                  />
                </Box>
              );
            });
          })}
        </Box>
      );
    } else {
      return (
        <ChatMessage
          doNotUseGlobalSearch={doNotUseGlobalSearch}
          externalNavigate={externalNavigate}
          filterVal={filterVal ?? ''}
          message={attachment}
          isActivityItem
          activityActor={user}
          isProject={isProject}
        />
      );
    }
  }

  if ((audit.documents?.length ?? 0) > 0) {
    if (doNotUseGlobalSearch && isDocumentType(attachment)) {
      const doc = attachment as Document;
      return (
        <Box>
          <Box marginBottom='xxs' pb={isWeb ? 'xxxs' : 'none'}>
            <ActivityItemDocuments
              isActivityItem
              isDocumentType
              isTask={false}
              documents={audit.documents}
              actor={user}
            />
          </Box>
          {doc.projectDocuments?.map((item) => (
            <Box key={item?.project?.id}>
              <TagsCollection
                doNotUseGlobalSearch={true}
                externalNavigate={externalNavigate}
                filterVal={filterVal ?? ''}
                showAuthor={false}
                borderless
                activityMode={activityMode}
                isRow
                isActivityItem
                showProject={!isProject}
                tagsCollection={{
                  tasks:
                    doc.taskDocuments
                      ?.filter(
                        (t) => t.task && item?.project?.id === t.task.projectId
                      )
                      ?.map((t) => t.task) ?? [],
                  project: item?.project,
                  author: user,
                }}
              />
            </Box>
          ))}
        </Box>
      );
    }
    return (
      <ActivityItemDocuments
        isActivityItem
        isTask={false}
        documents={audit.documents}
        actor={user}
      />
    );
  }

  if (isTaskType(attachment)) {
    return (
      <TagsCollection
        doNotUseGlobalSearch={doNotUseGlobalSearch}
        externalNavigate={externalNavigate}
        filterVal={filterVal ?? ''}
        showAuthor={false}
        borderless
        activityMode={activityMode}
        isRow
        isActivityItem
        showProject={!isProject}
        tagsCollection={{
          tasks: [attachment],
          project: attachment.project,
          author: user,
        }}
      />
    );
  }

  if (doNotUseGlobalSearch && _isProjectType(attachment)) {
    return (
      <TagsCollection
        doNotUseGlobalSearch={doNotUseGlobalSearch}
        externalNavigate={externalNavigate}
        filterVal={filterVal ?? ''}
        showAuthor={false}
        borderless
        activityMode={activityMode}
        isRow
        isActivityItem
        showProject={!isProject}
        tagsCollection={{
          tasks: [],
          project: attachment,
          author: user,
        }}
      />
    );
  }

  if (isTaskUserType(attachment)) {
    return (
      <TagsCollection
        doNotUseGlobalSearch={doNotUseGlobalSearch}
        externalNavigate={externalNavigate}
        filterVal={filterVal ?? ''}
        showAuthor={false}
        borderless
        activityMode={activityMode}
        isRow
        isActivityItem
        showProject={!isProject}
        tagsCollection={{
          tasks: [attachment.task],
          project: attachment.task.project,
          author: user,
        }}
      />
    );
  }

  if (!doNotUseGlobalSearch && isEntityType(attachment)) {
    if (
      isTaskAssigned ||
      isTaskDeleted ||
      isTaskUnassigned ||
      isTaskUnassignedSelf ||
      isTaskReassigned
    )
      return (
        <TagsCollection
          doNotUseGlobalSearch={doNotUseGlobalSearch}
          externalNavigate={externalNavigate}
          filterVal={filterVal ?? ''}
          showAuthor={false}
          borderless
          activityMode={activityMode}
          isRow
          isActivityItem
          showProject={!isProject}
          tagsCollection={{
            tasks: attachment.entityTask ? [attachment.entityTask] : [],
            project: attachment.entityProject || attachment.entityTask?.project,
            author: user,
          }}
        />
      );
  }

  if (doNotUseGlobalSearch && isEntityType(attachment)) {
    if (
      isTaskAssigned ||
      isTaskDeleted ||
      isTaskUnassigned ||
      isTaskUnassignedSelf ||
      isTaskReassigned ||
      isProjectAssigned
    )
      return (
        <TagsCollection
          doNotUseGlobalSearch={doNotUseGlobalSearch}
          externalNavigate={externalNavigate}
          filterVal={filterVal ?? ''}
          showAuthor={false}
          borderless
          activityMode={activityMode}
          isRow
          isActivityItem
          showProject={!isProject}
          tagsCollection={{
            tasks: attachment.entityTask ? [attachment.entityTask] : [],
            project: attachment.entityProject || attachment.entityTask?.project,
            author: user,
          }}
        />
      );
  }

  return null;
};

export default ProjectActivityItem;
